import { useEffect, useMemo, useState } from "react";
import { sortTeams } from "@/lib/ageOrder";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, ClipboardCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RegTypePicker, TeamSelect } from "./RegTypePicker";

const AGES = ["U6","U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","Adults"];
const KIT_SLOTS = [
  { key: "shirt", label: "Shirt", options: [["home", "Home shirt", /home/i], ["gk", "GK shirt", /gk shirt/i]] },
  { key: "away", label: "Away shirt", options: [["away", "Away shirt", /away/i]] },
  { key: "shorts", label: "Shorts", options: [["home", "Home shorts", /short/i], ["gk", "GK shorts", /gk short/i]] },
  { key: "socks", label: "Socks", options: [["home", "Home socks", /sock/i], ["gk", "GK sock", /gk sock/i]] },
];
const empty = {
  reg_type: "new", from_team_id: "",
  fa_number: "", first_name: "", last_name: "", date_of_birth: "", year_group: "", team_id: "",
  parent_first_name: "", parent_last_name: "", parent_email: "", parent_phone: "",
  kit_required: false, squad_number: "", notes: "",
};

function Field({ label, k, type = "text", placeholder, required, disabled, form, setForm }) {
  return (
    <div>
      <Label className="text-[10px] tracking-widest uppercase opacity-70">{label}{required && !disabled && <span className="text-[#83B68B]"> *</span>}</Label>
      <Input type={type} value={form[k]} disabled={disabled} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} placeholder={placeholder} data-testid={`reg-${k.replace(/_/g, "-")}`} className={disabled ? "opacity-50" : ""} />
    </div>
  );
}

export function RegisterPlayerDialog({ open, onClose, onSaved, teams, clubTeams: clubTeamsProp, catalogue, editing }) {
  const [form, setForm] = useState(empty);
  const isNew = form.reg_type === "new";
  const clubTeams = clubTeamsProp?.length ? clubTeamsProp : teams;
  const [kit, setKit] = useState({}); // slot -> { item_name, color, size }
  const [dobInfo, setDobInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKit({});
    setForm(editing ? { ...empty, ...Object.fromEntries(Object.keys(empty).map(k => [k, editing[k] ?? empty[k]])), kit_required: false } : empty);
  }, [open, editing]);

  useEffect(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date_of_birth)) { setDobInfo(null); return; }
    api.get("/registrations/year-group", { params: { dob: form.date_of_birth } })
      .then(({ data }) => { setDobInfo(data); setForm(f => ({ ...f, year_group: f._ygManual && f.year_group ? f.year_group : data.year_group })); })
      .catch(() => setDobInfo(null));
  }, [form.date_of_birth]);

  const under18 = dobInfo ? dobInfo.under_18 : true;
  const [allTeams, setAllTeams] = useState(false);
  const sortedTeams = useMemo(() => {
    const yg = form.year_group;
    const list = sortTeams(teams).sort((a, b) => ((a.age_group === yg) === (b.age_group === yg) ? 0 : a.age_group === yg ? -1 : 1));
    const inYg = list.filter(t => t.age_group === yg);
    return yg && !allTeams && inYg.length ? inYg : list;
  }, [teams, form.year_group, allTeams]);
  useEffect(() => { if (isNew && form.team_id && !sortedTeams.some(t => t.id === form.team_id)) setForm(f => ({ ...f, team_id: "" })); }, [sortedTeams]);

  const kitItems = useMemo(() => (catalogue || []).filter(c => c.active !== false && (c.category || "kit") === "kit"), [catalogue]);
  const itemFor = (re) => kitItems.find(c => re.test(c.item_name));
  const pickSlot = (slot, optKey) => {
    const opt = KIT_SLOTS.find(s => s.key === slot).options.find(o => o[0] === optKey);
    if (!opt) { setKit(k => { const n = { ...k }; delete n[slot]; return n; }); return; }
    const gk = optKey === "gk";
    const item = slot === "shirt" ? itemFor(gk ? /gk shirt/i : /home\)|home shirt/i)
      : slot === "away" ? itemFor(/away/i)
      : slot === "shorts" ? itemFor(gk ? /gk short/i : /hero short|short(?!.*gk)/i)
      : itemFor(gk ? /gk sock/i : /rayon sock|socks/i);
    if (!item) { toast.error(`No ${opt[1]} in the catalogue`); return; }
    setKit(k => ({ ...k, [slot]: { item_name: item.item_name, color: (item.colors || [])[0] || "", size: "", _item: item, _opt: optKey } }));
  };

  const myIds = useMemo(() => new Set((teams || []).map(t => t.id)), [teams]);
  const coachesOne = !isNew ? (myIds.has(form.team_id) || myIds.has(form.from_team_id)) : true;
  const canSubmit = form.fa_number.trim() && form.first_name.trim() && form.last_name.trim() && form.date_of_birth && form.team_id
    && (isNew || (form.from_team_id && form.from_team_id !== form.team_id && coachesOne))
    && (!under18 || (form.parent_first_name && form.parent_last_name && form.parent_email && form.parent_phone))
    && (isNew ? (!form.kit_required || Object.values(kit).length > 0) : true);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...form, year_group: form.year_group || null, notes: form.notes || null, squad_number: form.squad_number || null,
        from_team_id: isNew ? null : form.from_team_id, kit_required: isNew ? form.kit_required : false,
        parent_first_name: form.parent_first_name || null, parent_last_name: form.parent_last_name || null,
        parent_email: form.parent_email || null, parent_phone: form.parent_phone || null,
        kit_items: isNew && form.kit_required ? Object.values(kit).map(k => ({ item_name: k.item_name, color: k.color || null, size: k.size || null })) : [] };
      delete body._ygManual;
      const { data } = editing ? await api.put(`/registrations/${editing.id}`, body) : await api.post("/registrations", body);
      toast.success(editing ? "Registration updated" : !isNew ? `${data.reg_type_label} requested${data.stage === "coach_approval" ? " · waiting for the other team's head coach" : ""}` : `${data.first_name} registered${data.kit_order_ref ? ` · kit order ${data.kit_order_ref} raised` : ""}`);
      onSaved?.(data); onClose?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(false); }
  };


  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="register-player-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#83B68B]" /> {editing ? "Edit player details" : "Register a player"}</DialogTitle>
          <DialogDescription className="text-xs opacity-70">{isNew ? "The Registrations team will take it from here — you'll be emailed at every stage." : "The head coach of the other team approves in-app, then the Registrations team completes the league paperwork. No kit or payment proof needed."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {!editing && <RegTypePicker value={form.reg_type} onChange={v => setForm(f => ({ ...f, reg_type: v, kit_required: false, from_team_id: "", team_id: "" }))} />}
          {!isNew && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border border-border p-3" data-testid="reg-move-section">
              <TeamSelect label={form.reg_type === "transfer" ? "From (current team)" : "Current team"} value={form.from_team_id} onChange={v => setForm(f => ({ ...f, from_team_id: v }))} teams={clubTeams} exclude={form.team_id} testId="reg-from-team" />
              <TeamSelect label={form.reg_type === "transfer" ? "To (new team)" : "Second team"} value={form.team_id} onChange={v => setForm(f => ({ ...f, team_id: v }))} teams={clubTeams} exclude={form.from_team_id} testId="reg-team" />
              {!coachesOne && form.team_id && form.from_team_id && <div className="sm:col-span-2 text-[11px] text-amber-300" data-testid="reg-move-warning">You must lead or coach one of these two teams.</div>}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="FA number" k="fa_number" required placeholder="e.g. 12345678" form={form} setForm={setForm} />
            <Field label="Date of birth" k="date_of_birth" type="date" required form={form} setForm={setForm} />
            <Field label="First name" k="first_name" required form={form} setForm={setForm} />
            <Field label="Surname" k="last_name" required form={form} setForm={setForm} />
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Year group {dobInfo && <span className="text-[#83B68B] normal-case tracking-normal">· auto {dobInfo.year_group}</span>}</Label>
              <Select value={form.year_group || ""} onValueChange={v => setForm(f => ({ ...f, year_group: v, _ygManual: v !== dobInfo?.year_group }))}>
                <SelectTrigger data-testid="reg-year-group"><SelectValue placeholder="Enter date of birth first" /></SelectTrigger>
                <SelectContent>{AGES.map(a => <SelectItem key={a} value={a}>{a}{dobInfo?.year_group === a ? " · from DOB" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {isNew && <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70 flex justify-between">Team <span className="text-[#83B68B]">*</span>
                {form.year_group && <button type="button" onClick={() => setAllTeams(a => !a)} className="normal-case tracking-normal underline opacity-70 hover:opacity-100" data-testid="reg-team-toggle-all">{allTeams ? `Only ${form.year_group} teams` : "Show all teams"}</button>}
              </Label>
              <Select value={form.team_id} onValueChange={v => setForm(f => ({ ...f, team_id: v }))}>
                <SelectTrigger data-testid="reg-team"><SelectValue placeholder="Pick a team" /></SelectTrigger>
                <SelectContent>{sortedTeams.map(t => <SelectItem key={t.id} value={t.id}>{t.age_group} · {t.name}{t.age_group === form.year_group ? " ✓" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>}
          </div>

          <div className={`rounded-md border border-border p-3 space-y-3 ${under18 ? "" : "opacity-60"}`} data-testid="reg-parent-section">
            <div className="text-[10px] tracking-widest uppercase opacity-60">Parent / guardian {under18 ? "(required under 18)" : "— not needed, player is 18 or over"}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="First name" k="parent_first_name" required disabled={!under18} form={form} setForm={setForm} />
              <Field label="Surname" k="parent_last_name" required disabled={!under18} form={form} setForm={setForm} />
              <Field label="Email" k="parent_email" type="email" required disabled={!under18} form={form} setForm={setForm} />
              <Field label="Phone" k="parent_phone" required placeholder="07…" disabled={!under18} form={form} setForm={setForm} />
            </div>
          </div>

          {!editing && isNew && (
            <div className="rounded-md border border-border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">Kit required?</div>
                  <div className="text-[11px] opacity-60">Raises a kit order in your name for this player.</div>
                </div>
                <Switch checked={form.kit_required} onCheckedChange={v => setForm({ ...form, kit_required: v })} data-testid="reg-kit-required" />
              </div>
              {form.kit_required && (
                <div className="space-y-3" data-testid="reg-kit-section">
                  <Field label="Squad number" k="squad_number" placeholder="e.g. 9" form={form} setForm={setForm} />
                  {KIT_SLOTS.map(s => (
                    <div key={s.key} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                      <div>
                        <Label className="text-[10px] tracking-widest uppercase opacity-70">{s.label}</Label>
                        <Select value={kit[s.key]?._opt || "none"} onValueChange={v => pickSlot(s.key, v === "none" ? null : v)}>
                          <SelectTrigger data-testid={`reg-kit-${s.key}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="none">Not needed</SelectItem>{s.options.map(o => <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {kit[s.key] && (
                        <>
                          <div>
                            <Label className="text-[10px] tracking-widest uppercase opacity-70">Colour</Label>
                            <Select value={kit[s.key].color || ""} onValueChange={v => setKit(k => ({ ...k, [s.key]: { ...k[s.key], color: v } }))}>
                              <SelectTrigger data-testid={`reg-kit-${s.key}-color`}><SelectValue placeholder="Colour" /></SelectTrigger>
                              <SelectContent>{(kit[s.key]._item.colors || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[10px] tracking-widest uppercase opacity-70">Size</Label>
                            <Select value={kit[s.key].size || ""} onValueChange={v => setKit(k => ({ ...k, [s.key]: { ...k[s.key], size: v } }))}>
                              <SelectTrigger data-testid={`reg-kit-${s.key}-size`}><SelectValue placeholder="Size" /></SelectTrigger>
                              <SelectContent>{(kit[s.key]._item.sizes || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Notes for the Registrations team</Label>
            <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="reg-notes" />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40" data-testid="reg-cancel">Cancel</button>
            <button type="button" onClick={submit} disabled={!canSubmit || busy} data-testid="reg-submit" className="btn-volt flex-1 py-3 rounded-md disabled:opacity-40 inline-flex items-center justify-center gap-2">
              <ClipboardCheck className="w-4 h-4" /> {busy ? "Saving…" : editing ? "Save changes" : isNew ? "Register player" : form.reg_type === "transfer" ? "Request transfer" : "Request dual registration"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
